import {
  calculatePercentageMoney,
  roundMoney,
  sumMoney,
} from './money';

export type PurchasePriceMode = 'net' | 'gross';
export type SalesVatMode = 'no_vat' | 'vat_8' | 'vat_23' | 'confirm';
export type MaterialSide = 'client' | 'contractor' | 'info';

export interface ContractorFinancialSettings {
  vatPayer: boolean;
  purchasePriceMode: PurchasePriceMode;
  salesVatMode: SalesVatMode;
  materialSide: MaterialSide;
}

export interface MaterialFinancialResult {
  materialCostForMargin: number;
  deductibleVat: number;
  clientMaterialPrice: number;
  pdfMaterialTotal: number;
  purchaseNet: number;
  purchaseGross: number;
  note: string;
}

export const DEFAULT_CONTRACTOR_FINANCIAL_SETTINGS: ContractorFinancialSettings = {
  vatPayer: false,
  purchasePriceMode: 'gross',
  salesVatMode: 'no_vat',
  materialSide: 'client',
};

const PURCHASE_VAT_RATE = 0.23;

export function normalizeContractorFinancialSettings(
  input: Partial<ContractorFinancialSettings> | null | undefined
): ContractorFinancialSettings {
  const vatPayer = typeof input?.vatPayer === 'boolean'
    ? input.vatPayer
    : DEFAULT_CONTRACTOR_FINANCIAL_SETTINGS.vatPayer;
  const purchasePriceMode =
    input?.purchasePriceMode === 'net' || input?.purchasePriceMode === 'gross'
      ? input.purchasePriceMode
      : vatPayer
        ? 'net'
        : 'gross';
  const salesVatMode =
    input?.salesVatMode === 'no_vat' ||
    input?.salesVatMode === 'vat_8' ||
    input?.salesVatMode === 'vat_23' ||
    input?.salesVatMode === 'confirm'
      ? input.salesVatMode
      : vatPayer
        ? 'vat_23'
        : 'no_vat';
  const materialSide =
    input?.materialSide === 'client' ||
    input?.materialSide === 'contractor' ||
    input?.materialSide === 'info'
      ? input.materialSide
      : DEFAULT_CONTRACTOR_FINANCIAL_SETTINGS.materialSide;

  return {
    vatPayer,
    purchasePriceMode,
    salesVatMode,
    materialSide,
  };
}

export function validateContractorFinancialSettings(
  settings: ContractorFinancialSettings
): string[] {
  const errors: string[] = [];

  if (!settings.vatPayer && settings.purchasePriceMode === 'net') {
    errors.push('Nie-VAT-owiec rozlicza koszt zakupu materiałów w cenie brutto.');
  }
  if (
    !settings.vatPayer &&
    (settings.salesVatMode === 'vat_8' || settings.salesVatMode === 'vat_23')
  ) {
    errors.push('Nie-VAT-owiec nie może doliczać VAT do ceny sprzedaży.');
  }

  return errors;
}

export function calculateMaterialFinancials(input: {
  settings: ContractorFinancialSettings;
  purchasePrice: number;
  clientMaterialPrice: number;
  purchaseVatRate?: number;
}): MaterialFinancialResult {
  const errors = validateContractorFinancialSettings(input.settings);
  if (errors.length > 0) {
    throw new Error(errors.join(' '));
  }

  if (input.settings.materialSide !== 'contractor') {
    return {
      materialCostForMargin: 0,
      deductibleVat: 0,
      clientMaterialPrice: 0,
      pdfMaterialTotal: 0,
      purchaseNet: 0,
      purchaseGross: 0,
      note:
        input.settings.materialSide === 'client'
          ? 'Materiały po stronie klienta.'
          : 'Materiały pokazane informacyjnie, bez doliczania do oferty.',
    };
  }

  const purchaseVatRate =
    typeof input.purchaseVatRate === 'number' &&
    Number.isFinite(input.purchaseVatRate) &&
    input.purchaseVatRate >= 0
      ? input.purchaseVatRate
      : PURCHASE_VAT_RATE;
  const purchasePrice = roundMoney(Math.max(0, input.purchasePrice));
  const purchaseNet =
    input.settings.purchasePriceMode === 'net'
      ? purchasePrice
      : roundMoney(purchasePrice / (1 + purchaseVatRate));
  const purchaseGross =
    input.settings.purchasePriceMode === 'gross'
      ? purchasePrice
      : sumMoney([
          purchaseNet,
          calculatePercentageMoney(purchaseNet, purchaseVatRate),
        ]);
  const deductibleVat = input.settings.vatPayer
    ? sumMoney([purchaseGross, -purchaseNet])
    : 0;
  const clientMaterialPrice = roundMoney(Math.max(0, input.clientMaterialPrice));

  return {
    materialCostForMargin: input.settings.vatPayer ? purchaseNet : purchaseGross,
    deductibleVat,
    clientMaterialPrice,
    pdfMaterialTotal: clientMaterialPrice,
    purchaseNet,
    purchaseGross,
    note: 'Materiały zapewnia wykonawca.',
  };
}
